// src/components/Login.js
import React, { useState, useEffect } from "react";
import {
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Box,
  Snackbar,
  Alert,
  LinearProgress,
  InputAdornment,
  IconButton,
  Fade,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Login as LoginIcon,
} from "@mui/icons-material";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase/Config";
import { useNavigate } from 'react-router-dom';

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [errors, setErrors] = useState({ email: "", password: "" });
  const navigate = useNavigate();

  // Verificar se o usuário já está autenticado
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        // Usuário já está autenticado, redirecionar para home
        navigate('/home');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Função de validação
  const validateForm = () => {
    const newErrors = { email: "", password: "" };
    let isValid = true;

    // Validação de email
    if (!email.trim()) {
      newErrors.email = "O email é obrigatório";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Digite um email válido";
      isValid = false;
    }

    // Validação de senha
    if (!password) {
      newErrors.password = "A senha é obrigatória";
      isValid = false;
    } else if (password.length < 6) {
      newErrors.password = "A senha deve ter pelo menos 6 caracteres";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Função para traduzir erros do Firebase
  const getFirebaseErrorMessage = (errorCode) => {
    const errorMessages = {
      'auth/invalid-email': 'Email inválido',
      'auth/user-disabled': 'Esta conta foi desativada',
      'auth/user-not-found': 'Email ou senha incorretos',
      'auth/wrong-password': 'Email ou senha incorretos',
      'auth/invalid-credential': 'Email ou senha incorretos',
      'auth/too-many-requests': 'Muitas tentativas de login. Tente novamente mais tarde',
      'auth/network-request-failed': 'Erro de conexão. Verifique sua internet',
      'auth/operation-not-allowed': 'Operação não permitida',
      'auth/weak-password': 'Senha muito fraca',
    };
    return errorMessages[errorCode] || 'Erro ao fazer login. Tente novamente';
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setMessage({ 
        text: "Por favor, corrija os erros no formulário", 
        type: "error" 
      });
      return;
    }

    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      setMessage({ text: "Login realizado com sucesso!", type: "success" });
      setTimeout(() => {
        navigate('/home');
      }, 1000);
    } catch (err) {
      console.error("Erro de login:", err);
      const errorMessage = getFirebaseErrorMessage(err.code);
      setMessage({ text: errorMessage, type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setMessage({ text: "", type: "" });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        position: 'relative',
        paddingBottom: '80px',
      }}
    >
      <Fade in={true} timeout={800}>
        <Paper
          elevation={24}
          sx={{
            padding: { xs: 3, sm: 5 },
            width: '100%',
            maxWidth: 480,
            borderRadius: 4,
            background: '#ffffff',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}
        >
          {/* Logo e Título */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              component="img"
              src="EMO_LOGO.png"
              alt="Logo AAP-GIS"
              sx={{
                width: 180,
                height: 'auto',
                mb: 3,
                filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))',
              }}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <LoginIcon
                sx={{
                  fontSize: 36,
                  color: '#48bb78',
                  mr: 1,
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Bem-vindo!
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary">
              Plataforma de Gestão AAP-GIS
            </Typography>
          </Box>

          {/* Indicador de Progresso */}
          {loading && (
            <LinearProgress
              sx={{
                mb: 3,
                borderRadius: 1,
                height: 6,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #48bb78 0%, #38a169 100%)',
                },
              }}
            />
          )}

          {/* Formulário */}
          <form onSubmit={handleLogin}>
            <TextField
              label="Email"
              placeholder="seu.email@exemplo.com"
              variant="outlined"
              fullWidth
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors({ ...errors, email: "" });
              }}
              error={!!errors.email}
              helperText={errors.email}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: '#48bb78' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#48bb78',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#48bb78',
                  },
                },
              }}
            />

            <TextField
              label="Senha"
              placeholder="Digite sua senha"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              fullWidth
              required
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password) setErrors({ ...errors, password: "" });
              }}
              error={!!errors.password}
              helperText={errors.password}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: '#48bb78' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#48bb78',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#48bb78',
                  },
                },
              }}
            />

            {/* Link Esqueceu a Senha */}
            <Box sx={{ textAlign: 'right', mb: 3 }}>
              <Link
                href="/password-reset"
                underline="hover"
                sx={{
                  color: '#48bb78',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  '&:hover': {
                    color: '#38a169',
                  },
                }}
              >
                Esqueceu a senha?
              </Link>
            </Box>

            {/* Botão de Login */}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                py: 1.5,
                fontSize: 16,
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                boxShadow: '0 4px 15px rgba(72, 187, 120, 0.4)',
                transition: 'all 0.3s',
                '&:hover': {
                  background: 'linear-gradient(135deg, #38a169 0%, #48bb78 100%)',
                  boxShadow: '0 6px 20px rgba(72, 187, 120, 0.6)',
                  transform: 'translateY(-2px)',
                },
                '&:disabled': {
                  background: '#cccccc',
                  boxShadow: 'none',
                },
              }}
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>

          {/* Informações Adicionais */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Acesso restrito a usuários autorizados
            </Typography>
          </Box>
        </Paper>
      </Fade>

      {/* Copyright */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          textAlign: 'center',
          py: 2,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'rgba(255, 255, 255, 0.95)',
            fontWeight: 500,
            textShadow: '0 1px 2px rgba(0,0,0,0.2)',
          }}
        >
          © 2025 AAP-GIS. Todos os direitos reservados.
        </Typography>
      </Box>

      {/* Snackbar para Mensagens */}
      <Snackbar
        open={!!message.text}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={message.type}
          variant="filled"
          sx={{
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {message.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Login;
